import React from 'react';
import { SearchProps } from '../../../types/DesignSystem/Search';
import Typography from '../Typography/Typography';

const Search: React.FC<SearchProps> = ({
  placeholder = 'Search...',
  value,
  onChange,
  onSearch,
  disabled = false,
  clearable = true,
  size = 'medium',
  variant = 'default',
  className = '',
  helperText,
  error = false,
}) => {
  const sizeClasses = {
    small: 'tw-px-2 tw-py-1 tw-text-sm',
    medium: 'tw-px-3 tw-py-2 tw-text-base',
    large: 'tw-px-4 tw-py-3 tw-text-lg',
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      onSearch?.(value);
    }
  };

  const handleClear = () => {
    onChange('');
    onSearch?.('');
  };

  return (
    <div className={`tw-flex tw-flex-col tw-gap-2 ${className}`}>
      <div
        className={`tw-flex tw-items-center tw-gap-2 tw-rounded tw-border tw-transition-colors ${
          error
            ? 'tw-border-red-500 tw-bg-red-50'
            : variant === 'bordered'
              ? 'tw-border-grey-300 tw-bg-white'
              : 'tw-border-grey-200 tw-bg-grey-50'
        } ${
          disabled
            ? 'tw-opacity-50 tw-cursor-not-allowed'
            : 'focus-within:tw-border-blue-500 focus-within:tw-bg-white'
        }`}
      >
        <svg
          className={`tw-w-5 tw-h-5 tw-flex-shrink-0 tw-pl-1 ${
            error ? 'tw-text-red-500' : 'tw-text-grey-500'
          }`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
          />
        </svg>

        <input
          type="text"
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyPress={handleKeyPress}
          disabled={disabled}
          className={`tw-flex-1 tw-bg-transparent tw-outline-none tw-text-grey-900 ${sizeClasses[size]} placeholder:tw-text-grey-400 disabled:tw-cursor-not-allowed`}
          aria-invalid={error}
          aria-describedby={helperText ? 'search-helper' : undefined}
        />

        {clearable && value && !disabled && (
          <button
            onClick={handleClear}
            className="tw-p-1 tw-text-grey-400 hover:tw-text-grey-600 tw-transition-colors tw-flex-shrink-0"
            aria-label="Clear search"
            type="button"
          >
            <svg
              className="tw-w-4 tw-h-4"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        )}
      </div>

      {helperText && (
        <div id="search-helper">
          <Typography
            variant="caption-metadata"
            className={error ? 'tw-text-red-500' : 'tw-text-grey-600'}
          >
            {helperText}
          </Typography>
        </div>
      )}
    </div>
  );
};

export default Search;
